import { useRef } from "react";
import { motion, useReducedMotion, useTransform } from "framer-motion";
import { useHeroParallax } from "./motion";

/** Rows converge on a vanishing point to give the near field depth. */
const ROWS = Array.from({ length: 19 }, (_, i) => -900 + i * 190);

function Grass({ x, h = 60, w = 6, delay = 0 }) {
  return <path className="sway" style={{ animationDelay: `${delay}s`, transformOrigin: `${x}px 900px` }}
               d={`M${x} 900 Q${x + w} ${900 - h * 0.6} ${x + w * 0.4} ${900 - h} Q${x - w * 0.2} ${900 - h * 0.55} ${x - w} 900Z`} />;
}

export default function HeroScene({ children }) {
  const ref = useRef(null);
  const reduce = useReducedMotion();
  const p = useHeroParallax(ref);
  const range = [0, reduce ? 0 : 1];
  const sun = useTransform(p, [0, 1], [0, range[1] * 140]);
  const far = useTransform(p, [0, 1], [0, range[1] * 90]);
  const mid = useTransform(p, [0, 1], [0, range[1] * 170]);
  const near = useTransform(p, [0, 1], [0, range[1] * 260]);
  const fore = useTransform(p, [0, 1], [0, range[1] * 340]);
  const content = useTransform(p, [0, 1], [0, reduce ? 0 : -110]);
  const fade = useTransform(p, [0, 0.7], [1, reduce ? 1 : 0]);
  const glow = useTransform(p, [0, 1], [1, reduce ? 1 : 0.4]);

  return (
    <section ref={ref} className="relative overflow-hidden min-h-[100svh] flex flex-col" data-testid="hero">
      {/* sky */}
      <div className="absolute inset-0" style={{ background: "linear-gradient(180deg,#0b251e 0%,#12392f 28%,#1f5a47 52%,#c9894f 82%,#f1c27d 100%)" }} />
      {/* stars / pollen */}
      <div className="absolute inset-0 pointer-events-none" aria-hidden>
        {Array.from({ length: 22 }).map((_, i) => (
          <span key={i} className="absolute rounded-full bg-white/70 twinkle"
                style={{ left: `${(i * 47) % 100}%`, top: `${(i * 29) % 52}%`, width: 2 + (i % 3), height: 2 + (i % 3), animationDelay: `${(i % 7) * 0.6}s` }} />
        ))}
      </div>
      {/* sun */}
      <motion.div style={{ y: sun, opacity: glow }} className="absolute left-1/2 -translate-x-1/2 top-[34%] pointer-events-none" aria-hidden>
        <div className="w-[34rem] h-[34rem] sm:w-[46rem] sm:h-[46rem] rounded-full -translate-y-1/3"
             style={{ background: "radial-gradient(circle, rgba(255,226,160,.95) 0%, rgba(255,190,110,.55) 22%, rgba(255,160,90,.18) 45%, transparent 68%)" }} />
      </motion.div>

      {/* landscape layers */}
      <svg viewBox="0 0 1440 900" preserveAspectRatio="xMidYMax slice" className="absolute inset-x-0 bottom-0 w-full h-[78%] pointer-events-none" aria-hidden>
        <defs>
          <linearGradient id="farG" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stopColor="#3f7d63" /><stop offset="1" stopColor="#2c6650" /></linearGradient>
          <linearGradient id="midG" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stopColor="#2a6048" /><stop offset="1" stopColor="#1d4c3a" /></linearGradient>
          <linearGradient id="nearG" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stopColor="#1f5240" /><stop offset="1" stopColor="#123a2d" /></linearGradient>
          <clipPath id="field"><path d="M0 640 C 240 600 460 612 720 606 C 980 600 1200 590 1440 626 L1440 900 L0 900Z" /></clipPath>
          <filter id="haze"><feGaussianBlur stdDeviation="1.2" /></filter>
        </defs>
        <motion.g style={{ y: far }}>
          <path d="M0 520 C 180 440 330 470 480 500 C 640 535 760 430 930 450 C 1100 470 1250 530 1440 470 L1440 900 L0 900Z" fill="url(#farG)" opacity=".75" filter="url(#haze)" />
        </motion.g>
        <motion.g style={{ y: mid }}>
          <path d="M0 585 C 160 530 330 548 520 578 C 700 606 860 520 1040 536 C 1220 552 1330 600 1440 570 L1440 900 L0 900Z" fill="url(#midG)" />
          {/* trees */}
          {[1180, 1290, 1370].map((x, i) => (
            <g key={x} opacity=".9"><rect x={x - 2} y={560 - (i % 2) * 6} width="4" height="26" fill="#0f3025" /><ellipse cx={x} cy={548 - (i % 2) * 6} rx={15 + (i % 3) * 3} ry={22 + (i % 2) * 4} fill="#17432f" /></g>
          ))}
        </motion.g>
        <motion.g style={{ y: near }}>
          <path d="M0 640 C 240 600 460 612 720 606 C 980 600 1200 590 1440 626 L1440 900 L0 900Z" fill="url(#nearG)" />
          <g clipPath="url(#field)" stroke="#7fb28b" strokeOpacity=".22" strokeWidth="2.2" fill="none">
            {ROWS.map((x) => <line key={x} x1={x} y1="900" x2={720 + (x - 720) * 0.02} y2="600" />)}
          </g>
        </motion.g>
        <motion.g style={{ y: fore }} fill="#0a241b">
          <path d="M0 900 L0 790 C 160 760 320 800 520 815 C 720 830 900 780 1100 795 C 1260 806 1360 780 1440 770 L1440 900Z" />
          {Array.from({ length: 34 }).map((_, i) => <Grass key={i} x={i * 44 + (i % 3) * 7} h={42 + ((i * 17) % 46)} w={5 + (i % 3)} delay={(i % 9) * 0.25} />)}
        </motion.g>
      </svg>
      {/* readable gradient behind text + fade into page */}
      <div className="absolute inset-0 pointer-events-none" style={{ background: "linear-gradient(180deg, rgba(8,28,22,.55) 0%, rgba(8,28,22,0) 45%)" }} />
      <div className="absolute inset-x-0 bottom-0 h-40 pointer-events-none bg-gradient-to-b from-transparent to-background" />

      <motion.div style={{ y: content, opacity: fade }} className="relative z-10 flex-1 flex items-center">
        {children}
      </motion.div>
    </section>
  );
}
